interface iUseMobileDetect {
	isMobile: () => boolean;
	isDesktop: () => boolean;
	isAndroid: () => boolean;
	isIos: () => boolean;
	isSSR: () => boolean;
}

/**
 * Parses a user-agent string and returns device-detection predicates.
 * Kept as a pure function so it can be tested independently of React.
 * @param userAgent - The raw `navigator.userAgent` string.
 * @returns An object of boolean predicate functions.
 */
const getMobileDetect = (userAgent: string): iUseMobileDetect => {
	const isAndroid = (): boolean => Boolean(userAgent.match(/Android/i));
	const isIos = (): boolean => Boolean(userAgent.match(/iPhone|iPad|iPod/i));
	const isOpera = (): boolean => Boolean(userAgent.match(/Opera Mini/i));
	const isWindows = (): boolean => Boolean(userAgent.match(/IEMobile/i));
	const isSSR = (): boolean => Boolean(userAgent.match(/SSR/i));

	const isMobile = (): boolean => Boolean(isAndroid() || isIos() || isOpera() || isWindows());
	const isDesktop = (): boolean => Boolean(!isMobile() && !isSSR());
	return {
		isMobile,
		isDesktop,
		isAndroid,
		isIos,
		isSSR,
	};
};

/**
 * React hook that exposes device-type detection based on the current `navigator.userAgent`.
 * Falls back to `'SSR'` when `navigator` is unavailable (server-side rendering).
 *
 * @returns An {@link iUseMobileDetect} object with predicate functions:
 * - `isMobile()` — true for Android, iOS, Opera Mini, or Windows Phone.
 * - `isDesktop()` — true when not mobile and not SSR.
 * - `isAndroid()`, `isIos()`, `isSSR()` — platform-specific checks.
 *
 * @example
 * const { isMobile } = useMobileDetect();
 * return isMobile() ? <MobileView /> : <DesktopView />;
 */
export const useMobileDetect = (): iUseMobileDetect => {
	const userAgent = typeof navigator === 'undefined' ? 'SSR' : navigator.userAgent;
	return getMobileDetect(userAgent);
};
